<script setup lang="ts">
import { getLocalStorageReac, getSessionStorageReac } from "@/assets/ts/helpers";
import { useRoute } from "vue-router";
import type { Component } from "vue";
import srflag from "@/components/shared/langSwitcher/srflag.svg";
import enflag from "@/components/shared/langSwitcher/enflag.svg";
import arrowDown from "@/assets/image/icons/arrow-down.svg";
import { useDefaultStore } from "@/store";

const { locale } = useI18n();

export type TLangOption = {
  name: "en" | "sr";
  label: string;
  flag?: string; // URL to flag image (png / jpg)
  svgFlag?: Component; // URL to flag image (svg)
};

const props = withDefaults(
  defineProps<{
    selected?: TLangOption | TLangOption["name"] | null;
    options?: TLangOption[];
    setStorage?: boolean;
    canChangeRouter?: "query" | "params" | null;
    showCaret?: boolean;
    onlyToggle?: boolean;
  }>(),
  {
    selected: undefined,
    options: undefined,
    setStorage: true,
    canChangeRouter: undefined,
  }
);
const emit = defineEmits<{
  change: [value: TLangOption];
}>();

const sessionLang = getSessionStorageReac("lang", false);
const localLang = getLocalStorageReac("lang", false, "sr");
const route = useRoute();
const langSelectedInternal = ref<TLangOption | null>(null);
const dropdownOpen = ref(false);
const hovered = ref<string | null>(null);
const store = useDefaultStore();
const defaultOptions = ref<TLangOption[]>([
  {
    name: "sr",
    label: "SR",
    flag: srflag,
  },
  {
    name: "en",
    label: "EN",
    flag: enflag,
  },
]);

const langPropParsed = computed<TLangOption | null>(() => {
  if (!props.selected) return null;
  const langName = typeof props.selected === "string" ? props.selected : props.selected.name;
  const langObj = checkValidLang(langName);
  return langObj || null;
});
const langSelectedComputed = computed<TLangOption | null>(() => {
  return langPropParsed.value || langSelectedInternal.value;
});
const langOptions = computed(() => {
  return props.options?.length ? props.options : defaultOptions.value;
});

function checkValidLang(langName: TLangOption["name"]): TLangOption | null {
  const langObj = langOptions.value.find((langOpt) => langOpt.name === langName);
  if (langObj) {
    return langObj;
  }
  console.error("Invalid lang specified", langName);
  return null;
}
function initLangSelection() {
  const langStorage = localLang.value as TLangOption["name"];
  const routerLang = String(route.query.lang) as TLangOption["name"] | null;

  if (routerLang && props.canChangeRouter) {
    const langObj = checkValidLang(routerLang);
    langObj && setLanguage(langObj);
  } else if (langStorage) {
    const langObj = checkValidLang(langStorage);
    langObj && setLanguage(langObj);
  } else {
    if (langOptions.value[0]) setLanguage(langOptions.value[0]);
  }
}

function setLanguage(lang: TLangOption) {
  store.heroSectionKey++;
  console.log(":: Language changed to", lang);
  langSelectedInternal.value = lang;
  props.setStorage && (sessionLang.value = langSelectedInternal.value.name);
}
async function onInput(evt: TLangOption["name"]) {
  const langObj = checkValidLang(evt);
  if (langObj) {
    setLanguage(langObj);
  }
  if (langObj) emit("change", langObj);
  dropdownOpen.value = false;
}
function capitalize(val: string, shouldTransform = false) {
  if (shouldTransform) return val[0]?.toUpperCase() + val.slice(1).toLowerCase();
  return val;
}

/**
 * Only has affect when using toggle prop
 */
async function onClickWrapper() {
  if (props.onlyToggle) {
    await toggleOptions();
  }
}
async function toggleOptions() {
  const currIndex = langOptions.value.findIndex((opt) => opt.name === langSelectedInternal.value?.name);

  // Not selected or last in arr > 0
  const langObj = currIndex === -1 || currIndex === langOptions.value.length - 1 ? langOptions.value[0] : langOptions.value[currIndex + 1];

  if (langObj) {
    setLanguage(langObj);
    emit("change", langObj);
  }
}

defineExpose({
  setLanguage,
  toggleOptions,
});

onMounted(() => {
  if (props.selected) {
    const langName = typeof props.selected === "string" ? props.selected : props.selected.name;
    const langObj = checkValidLang(langName);
    langObj && setLanguage(langObj);
  } else {
    initLangSelection();
  }
});

watch(
  () => store.langSelected,
  (newLang) => {
    if (newLang) {
      localStorage.setItem("lang", newLang.name);
      locale.value = newLang.name;
    } else {
      const langName = getLocalStorageReac("lang", false, "sr").value as TLangOption["name"];
      const langObj = checkValidLang(langName);
      store.langSelected = langObj;
    }
  },
  { immediate: true }
);
</script>
<template>
  <div
    class="lang-switcher border-text-dark relative inline-block h-[54px] rounded-lg border-2 px-4 py-[15px]"
    :class="{ 'toggle-only': onlyToggle }"
    @click="onClickWrapper"
  >
    <button type="button" class="flex h-full w-full cursor-pointer items-center" @click.stop="dropdownOpen = !dropdownOpen">
      <!-- Label -->
      <span class="text-text-light text-sm">
        {{ capitalize(langSelectedComputed?.label || "") }}
      </span>

      <img v-if="langSelectedComputed?.flag" :src="langSelectedComputed.flag" class="ml-2 h-4 w-6 object-cover" alt="flag" />

      <div v-if="props.showCaret" class="bg-text-dark mx-3 h-full w-[2px]"></div>

      <img
        v-if="props.showCaret"
        :src="arrowDown"
        alt="Toggle services"
        class="transition-transform duration-300"
        :class="{ 'rotate-180': dropdownOpen }"
      />
    </button>

    <!-- Dropdown list -->
    <div
      v-if="dropdownOpen && !onlyToggle"
      class="bg-text-dark/80 absolute top-[calc(100%+4px)] right-0 z-10 w-full rounded-md border-2 border-[#494E5B] px-2 py-2 shadow-lg"
      :class="{ 'w-[110px]': !store.isMobile }"
    >
      <ul class="py-1">
        <li
          v-for="opt in langOptions"
          :key="opt.name"
          :class="{
            hovered: hovered === opt.name,
            'bg-text-light': hovered === opt.name,
          }"
          class="text-text-light flex cursor-pointer items-center gap-2 px-2 py-1 text-sm"
          @click.stop="onInput(opt.name)"
          @mouseover="hovered = opt.name"
          @mouseleave="hovered = null"
        >
          <span
            :class="{
              'text-text-dark': hovered === opt.name,
            }"
          >
            {{ capitalize(opt.label) }}
          </span>
          <img v-if="opt.flag" :src="opt.flag" class="h-4 w-6 object-cover" alt="flag" />
        </li>
      </ul>
    </div>
  </div>
</template>
